<template>
  <div class="w-100 d-flex flex-column justify-start align-start">
    <v-checkbox
      v-model="selectedItems"
      :label="applicantCountConfig.label"
      :value="applicantCountConfig"
      color="brandPrimary"
      hide-details
      class="mb-2"
      :disabled="exportInProgress"
    />
    <v-checkbox
      v-model="selectedItems"
      :label="candidateStageConfig.label"
      :value="candidateStageConfig"
      color="brandPrimary"
      hide-details
      class="mb-2"
      :disabled="exportInProgress"
    />
    <div
      v-if="candidateTableSelected"
      class="d-flex align-center mb-2"
    >
      <v-checkbox
        :label="candidateStageConfig.selectAllLabel"
        color="brandPrimary"
        :model-value="allCandidatesChildrenSelected"
        hide-details
        class="ml-7"
        :disabled="exportInProgress"
        @update:model-value="() => handleSelectAllCandidateStages(candidateStageConfig.children)"
      />
    </div>
    <div
      v-if="candidateTableSelected"
      class="d-flex flex-column align-start justify-start ml-7"
    >
      <v-checkbox
        v-for="option in candidateStageConfig.children"
        :key="option.label"
        v-model="selectedStages"
        :label="option.label"
        :value="option"
        color="brandPrimary"
        hide-details
        class="mb-2 ml-7"
        :disabled="exportInProgress"
      />
    </div>
    <DpplNewButton
      :disabled="buttonDisabled"
      color="brandPrimary"
      size="small"
      class="w-100 mt-4"
      @click="handleCsvDownload"
    >
      {{ buttonText }}
    </DpplNewButton>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import { useInsightsExport } from '@/stores/insights/export';
import { storeToRefs } from 'pinia';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { wait } from '@/helpers/wait';
import { useTracking } from '@/stores/tracking/tracking';
import { useRoute } from 'vue-router';

const route = useRoute();
const emits = defineEmits(['close']);

const props = defineProps({
  exportConfig: {
    type: Object,
    required: true,
  },
});

const { exportInProgress } = storeToRefs(useInsightsExport());

const { updateExportInProgress } = useInsightsExport();
const {
  addErrorSnackbar,
  addSuccessSnackbar,
} = useAppSnackbar();

const selectedItems = ref([]);
const selectedStages = ref([]);
const csvConfigs = computed(() => props.exportConfig.csvOptions || []);

const applicantCountConfig = csvConfigs.value.find((config) => config.label === 'Applicant Count Table');
const candidateStageConfig = csvConfigs.value.find((config) => config.label === 'Active Candidate Table');

const buttonDisabled = computed(() => exportInProgress.value
  || !selectedItems.value.length
  || (selectedItems.value.includes(candidateStageConfig) && !selectedStages.value.length));

const candidateTableSelected = computed(() => selectedItems.value
  .includes(candidateStageConfig));

const allCandidatesChildrenSelected = computed(() => {
  if (!candidateStageConfig || !candidateStageConfig.children) return false;
  return candidateStageConfig.children.every((child) => selectedStages.value.includes(child));
});

const buttonText = computed(() => {
  if (exportInProgress.value) return 'Downloading...';
  return `Download CSV${selectedItems.value.length > 1 ? 's' : ''}`;
});

const handleCsvDownload = async () => {
  const selectedStageLabels = selectedStages.value.map((stage) => stage.stage);

  const promises = selectedItems.value.map((config) => {
    if (config.csvDownloadFunction.constructor.name === 'AsyncFunction') {
      return config.csvDownloadFunction(selectedStageLabels);
    }

    return new Promise((resolve) => {
      config.csvDownloadFunction(selectedStageLabels);
      resolve(wait(2000)); // Simulate download time in case it takes a second
    });
  });

  try {
    updateExportInProgress(true);

    await Promise.all(promises);

    selectedItems.value.forEach((item) => {
      useTracking().insightsDownloadReport({
        reportName: route.name,
        downloadType: 'Individual Widgets',
        downloadLocation: 'Download Modal',
        fileType: 'CSV',
        widgetName: item.label,
      });
    });

    addSuccessSnackbar({
      text: `CSV${selectedItems.value.length > 1 ? 's' : ''} downloaded successfully`,
    });
  } catch (error) {
    addErrorSnackbar({ text: 'Failed to download CSVs' });
    throw error;
  } finally {
    updateExportInProgress(false);
    emits('close');
  }
};
const handleSelectAllCandidateStages = (children) => {
  const allSelected = children.every((child) => selectedStages.value.includes(child));
  if (allSelected) {
    selectedStages.value = [];
  } else {
    selectedStages.value = children.map((child) => child);
  }
};

watch(() => candidateTableSelected.value, (newValue) => {
  if (newValue) {
    selectedStages.value = candidateStageConfig.children;
  } else {
    selectedStages.value = [];
  }
});
</script>

<style scoped lang="less">
:deep(.v-selection-control__input) {
  height: 20px;
  width: 20px;

  .v-icon {
    width: 20px;
    height: 20px;
  }

  input {
    width: 20px;
    height: 20px;
  }
}

:deep(.v-selection-control) {
  .v-selection-control__wrapper {
    height: 20px !important;
  }

  .v-label {
    font-size: 13px !important;
    font-weight: 500;
    line-height: 150%;
  }
}

:deep(.v-selection-control) {
  min-height: inherit !important;
}

:deep(.v-selection-control__input) > .v-icon {
  opacity: 1.0 !important;
}
</style>
